import {
  useAuth as useClerkAuth,
  useOrganization as useClerkOrganization,
  useUser,
} from "@clerk/clerk-react";
import { ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useOpenUrl } from "../../hooks/useOpenUrl";
import { useAuth } from "./UnifiedAuthProvider";

// This component acts as a bridge between Clerk's auth state and our unified auth state.
export function ClerkAuthObserver() {
  const { getToken } = useClerkAuth();
  const { isSignedIn, isLoaded: isUserLoaded } = useUser();
  const { organization: clerkOrganization, isLoaded: orgLoaded } =
    useClerkOrganization();
  const {
    authenticate,
    isAuthenticated: isUnifiedAuth,
    user: _user,
    organization: unifiedOrganization,
  } = useAuth();

  const openUrl = useOpenUrl();

  useEffect(() => {
    const syncAuth = async () => {
      // If Clerk is signed in but our unified auth is not, we need to sync.
      if (isUserLoaded && orgLoaded && isSignedIn && !isUnifiedAuth) {
        if (!clerkOrganization) {
          console.error("Failed to sync: No organization selected in Clerk");
          return;
        }

        try {
          console.log(
            "Clerk is ready and signed in, syncing with unified auth...",
          );
          console.log(
            "Organization:",
            clerkOrganization.id,
            clerkOrganization.name,
          );
          const token = await getToken({ template: "default" });
          if (token) {
            await authenticate({ clerk_token: token });
            console.log("Successfully synced Clerk auth to unified auth.");
          } else {
            console.error("Failed to get token from Clerk");
          }
        } catch (error) {
          console.error("Failed to sync Clerk token with unified auth:", error);
        }
      }
    };

    syncAuth();
  }, [
    isUserLoaded,
    orgLoaded,
    isSignedIn,
    isUnifiedAuth,
    clerkOrganization,
    getToken,
    authenticate,
  ]);

  // Re-sync authentication when organization changes (for organization switching)
  useEffect(() => {
    const resyncOnOrgChange = async () => {
      if (
        isUserLoaded &&
        orgLoaded &&
        isSignedIn &&
        isUnifiedAuth &&
        clerkOrganization &&
        unifiedOrganization &&
        clerkOrganization.id !== unifiedOrganization.id
      ) {
        try {
          console.log(
            `Organization changed from ${unifiedOrganization.id} to ${clerkOrganization.id}, re-syncing...`,
          );
          const token = await getToken({ template: "default" });
          if (token) {
            await authenticate({ clerk_token: token });
            console.log(
              "Successfully re-synced auth after organization change.",
            );
          }
        } catch (error) {
          console.error(
            "Failed to re-sync auth after organization change:",
            error,
          );
        }
      }
    };

    resyncOnOrgChange();
  }, [
    isUserLoaded,
    orgLoaded,
    isSignedIn,
    isUnifiedAuth,
    clerkOrganization,
    unifiedOrganization,
    getToken,
    authenticate,
  ]);

  // Show organization setup screen if user is signed in but has no organization
  if (isUserLoaded && orgLoaded && isSignedIn && !clerkOrganization) {
    const getDashboardUrl = () => {
      const dashUrl = import.meta.env.VITE_DASH_URL;
      if (!dashUrl) {
        throw new Error("VITE_DASH_URL is required but not set.");
      }
      return dashUrl;
    };

    const handleOpenDashboard = () => {
      const dashboardUrl = getDashboardUrl();
      openUrl(dashboardUrl);
    };

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md p-8">
          <div className="text-center space-y-6">
            <div>
              <h1 className="text-2xl font-semibold mb-2">
                Organization Required
              </h1>
              <p className="text-muted-foreground">
                You need to be part of an organization to use this application.
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Please visit the dashboard to create or join an organization,
                then return here.
              </p>

              <Button onClick={handleOpenDashboard} className="w-full">
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Dashboard
              </Button>

              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full"
              >
                Refresh After Setup
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // This component does not render anything when everything is working.
  return null;
}
